import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, RotateCcw, History as HistoryIcon, IndianRupee, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { ROUNDS } from '../lib/constants';

const scoreRing = (s) => {
    if (s >= 8) return '#10B981';
    if (s >= 5) return '#F59E0B';
    return '#EF4444';
};

export default function Results() {
    const { sessionId } = useParams();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/interviews/${sessionId}`);
                setSession(res.data);
            } catch (e) {
                setErr(e.response?.data?.detail || 'Failed to load session');
            } finally {
                setLoading(false);
            }
        })();
    }, [sessionId]);

    if (loading) {
        return (
            <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading your results…
            </div>
        );
    }
    if (err) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>;
    if (!session) return null;

    const roundAverages = {};
    for (const rn of ['1', '2', '3', '4']) {
        const r = session.rounds?.[rn] || {};
        const scores = r.scores || [];
        if (scores.length) roundAverages[rn] = scores.reduce((a, b) => a + b, 0) / scores.length;
    }
    const overall = session.overall_score ?? 0;

    return (
        <div className="space-y-6" data-testid="results-page">
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="brand-card p-6 md:p-10 text-center"
            >
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F5FD] px-3 py-1 text-xs font-semibold text-[#1C3A94]">
                    <Award className="h-3.5 w-3.5" /> SESSION COMPLETE
                </div>
                <h1 className="mt-3 text-2xl md:text-4xl font-extrabold text-[#111827]">
                    {session.course} — Interview Results
                </h1>
                <div className="mt-8 flex items-center justify-center">
                    <div
                        className="flex h-40 w-40 items-center justify-center rounded-full border-[10px]"
                        style={{ borderColor: scoreRing(overall) }}
                    >
                        <div className="text-center">
                            <div className="text-5xl font-extrabold text-[#111827]">{overall}</div>
                            <div className="text-xs font-semibold text-[#9CA3AF]">/ 10</div>
                        </div>
                    </div>
                </div>
                <p className="mt-6 max-w-2xl mx-auto text-sm text-[#6B7280] leading-relaxed">
                    {session.overall_feedback ||
                        'Overall feedback will appear here once the session is finalized.'}
                </p>

                {session.rejected && (
                    <div className="mt-6 mx-auto max-w-xl rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
                        <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{session.rejection_reason}</span>
                    </div>
                )}

                {session.final_salary && !session.rejected && (
                    <div className="mt-6 mx-auto inline-flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3">
                        <IndianRupee className="h-5 w-5 text-emerald-600" />
                        <div className="text-left">
                            <div className="text-xs font-semibold text-emerald-700">Final Salary Offer</div>
                            <div className="text-2xl font-extrabold text-[#111827]">
                                ₹{session.final_salary} <span className="text-sm font-semibold text-[#6B7280]">LPA</span>
                            </div>
                        </div>
                    </div>
                )}
            </motion.section>

            <section>
                <h2 className="text-lg font-bold text-[#111827]">Round-by-round breakdown</h2>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {ROUNDS.filter((r) => r.num <= 4).map((r) => {
                        const avg = roundAverages[String(r.num)];
                        const val = avg ? avg.toFixed(1) : '—';
                        return (
                            <div key={r.num} className="brand-card p-5 flex items-center gap-4">
                                <div
                                    className="flex h-16 w-16 items-center justify-center rounded-full border-4"
                                    style={{ borderColor: avg ? scoreRing(avg) : '#E5E7EB' }}
                                >
                                    <div className="text-lg font-extrabold text-[#111827]">{val}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94]">
                                        Round {r.num}
                                    </div>
                                    <div className="text-sm font-bold text-[#111827]">{r.name}</div>
                                    <div className="text-xs text-[#6B7280]">{r.desc}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <div className="flex flex-wrap justify-center gap-3">
                <Link to="/home" className="brand-btn-primary gap-2">
                    <RotateCcw className="h-4 w-4" /> Take another interview
                </Link>
                <Link to="/history" className="brand-btn-outline gap-2">
                    <HistoryIcon className="h-4 w-4" /> View history
                </Link>
            </div>
        </div>
    );
}
